import { useTranslation } from '../i18n';
import { useNbTheme } from '../hooks/useNbTheme';

export default function BeforeAfterNB() {
  const { t } = useTranslation();
  const { theme, colors } = useNbTheme();
  const isDark = theme === 'dark';

  const chaosRed = '#D64545';
  const clarityTeal = '#0D9488';

  // Theme-aware tinted backgrounds
  const beforeBg = isDark ? 'rgba(214,69,69,0.08)' : '#FFF5F5';
  const afterBg = isDark ? 'rgba(13,148,136,0.08)' : '#F0FDFA';

  const beforePoints = Array.from({ length: 4 }, (_, i) =>
    t(`beforeAfter.before.point${i + 1}`)
  );
  const afterPoints = Array.from({ length: 4 }, (_, i) =>
    t(`beforeAfter.after.point${i + 1}`)
  );

  const labelStyle = {
    fontFamily: "'Space Grotesk', system-ui, sans-serif",
    fontWeight: 700,
    fontSize: '0.75rem',
    letterSpacing: '0.1em',
    display: 'inline-block',
    padding: '4px 10px',
    border: '3px solid',
  };

  return (
    <section
      className="py-12 md:py-20"
      style={{ background: colors.bgPage }}
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="mb-8 md:mb-12">
          <h2
            className="font-nb font-bold mb-3"
            style={{
              color: colors.textPrimary,
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              lineHeight: 1.2,
            }}
          >
            {t('beforeAfter.title')}
          </h2>
          <p
            className="font-nb max-w-xl"
            style={{
              color: colors.textSecondary,
              fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
              lineHeight: 1.6,
            }}
          >
            {t('beforeAfter.subtitle')}
          </p>
        </div>

        {/* Split screen */}
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* BEFORE — Chaos side */}
          <div
            className="p-5 md:p-8 border-3 md:border-r-[1.5px] border-b-3 md:border-b-3"
            style={{
              background: beforeBg,
              borderColor: chaosRed,
            }}
          >
            <span
              style={{
                ...labelStyle,
                color: chaosRed,
                borderColor: chaosRed,
                background: 'transparent',
              }}
            >
              {t('beforeAfter.before.label')}
            </span>

            <h3
              className="font-nb font-bold mt-4 mb-5"
              style={{
                color: colors.textPrimary,
                fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
                lineHeight: 1.3,
              }}
            >
              {t('beforeAfter.before.headline')}
            </h3>

            <ul className="space-y-3">
              {beforePoints.map((point, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 font-nb text-sm md:text-base"
                  style={{ color: colors.textSecondary, lineHeight: 1.5 }}
                >
                  <span
                    className="flex-shrink-0 w-5 h-5 mt-0.5 flex items-center justify-center font-nb-mono font-bold text-xs"
                    style={{
                      border: `2px solid ${chaosRed}`,
                      color: chaosRed,
                    }}
                  >
                    ✕
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* AFTER — Clarity side */}
          <div
            className="p-5 md:p-8 border-3 md:border-l-[1.5px]"
            style={{
              background: afterBg,
              borderColor: clarityTeal,
            }}
          >
            <span
              style={{
                ...labelStyle,
                color: clarityTeal,
                borderColor: clarityTeal,
                background: 'transparent',
              }}
            >
              {t('beforeAfter.after.label')}
            </span>

            <h3
              className="font-nb font-bold mt-4 mb-5"
              style={{
                color: colors.textPrimary,
                fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
                lineHeight: 1.3,
              }}
            >
              {t('beforeAfter.after.headline')}
            </h3>

            <ul className="space-y-3">
              {afterPoints.map((point, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 font-nb text-sm md:text-base"
                  style={{ color: colors.textSecondary, lineHeight: 1.5 }}
                >
                  <span
                    className="flex-shrink-0 w-5 h-5 mt-0.5 flex items-center justify-center font-nb-mono font-bold text-xs"
                    style={{
                      border: `2px solid ${clarityTeal}`,
                      color: clarityTeal,
                    }}
                  >
                    ✓
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
